import { useEffect, useMemo, useState } from 'react'
import { Command } from 'cmdk'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  HashIcon,
  Notebook01Icon,
  PaintBrushIcon,
  Search01Icon,
  ServerStack01Icon,
  SourceCodeIcon,
  UserIcon,
  BellIcon,
  Comment,
  Contact01Icon,
  DatabaseSearchIcon,
  Folder01Icon,
  SaveIcon,
  Key01Icon
} from '@hugeicons/core-free-icons'
import {
  hideNotificationsAtom,
  hideConversationsAtom,
  hideFollowingAtom,
  hideFavoriteRelaysAtom
} from '@/atoms/preferences'
import { usePrimaryPage, useSecondaryPage } from '@/PageManager'
import { toNote, toProfile } from '@/lib/link'
import { normalizeUrl, simplifyUrl } from '@/lib/url'
import {
  addUsernameToIndex,
  searchProfiles,
  searchPubKeysFromLocal
} from '@/services/client.service'
import RelayIcon from '@/components/RelayIcon'
import UserItem from '@/components/UserItem'
import FlexSearch from 'flexsearch'
import { useTranslation } from 'react-i18next'
import { useAtomValue, useSetAtom } from 'jotai'
import { unwrappedFollowListAtom, unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { decode } from '@nostr/tools/nip19'
import { accountAtom, useCurrentPubkey } from '@/atoms/session'
import { openSaveNsecModal } from '@/atoms/key-backup'
import { includeRepliesAtom } from '@/atoms/feed'
import { switchFeed } from '@/PageManager'
import HardcodedFeedListItem from '@/components/HardcodedFeedListItem'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { resolveProfileInput } from '@/lib/pubkey'
import { isNip05, queryProfile } from '@nostr/tools/nip05'
import { pomegranateAuthAtom } from '@/atoms/pomegranate'
import { getErrorMessage } from '@/lib/error'

function useCmdk() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    const toggleHandler = () => setOpen((o) => !o)
    document.addEventListener('keydown', handler)
    document.addEventListener('quick-switch-toggle', toggleHandler)
    return () => {
      document.removeEventListener('keydown', handler)
      document.removeEventListener('quick-switch-toggle', toggleHandler)
    }
  }, [open])

  return { open, setOpen }
}

function CmdkStyles() {
  return (
    <style>{`
[cmdk-overlay] {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: hsl(0 0% 0% / 0.8);
}

[cmdk-dialog] {
  position: fixed;
  top: 20vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  width: 100%;
  max-width: 32rem;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--popover));
  color: hsl(var(--popover-foreground));
  box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.3), 0 4px 10px -6px rgb(0 0 0 / 0.2);
  overflow: hidden;
}

[cmdk-dialog]:focus-visible {
  outline: none;
}

[cmdk-input-wrapper] {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid hsl(var(--border));
}

[cmdk-input-wrapper] [cmdk-input] {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: hsl(var(--foreground));
}

[cmdk-input-wrapper] [cmdk-input]::placeholder {
  color: hsl(var(--muted-foreground));
}

[cmdk-list] {
  max-height: 30rem;
  overflow-y: auto;
  padding: 0.5rem;
  scroll-padding-block: 0.5rem;
}

[cmdk-list] [cmdk-empty] {
  padding: 1.5rem 0;
  text-align: center;
  font-size: 0.875rem;
  color: hsl(var(--muted-foreground));
}

[cmdk-group] {
  &:not(:first-of-type) {
    margin-top: 0.5rem;
  }
}

[cmdk-group] [cmdk-group-heading] {
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: hsl(var(--muted-foreground));
}

[cmdk-item] {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: calc(var(--radius) - 2px);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  user-select: none;
  outline: none;
}

[cmdk-item][data-selected=true] {
  background: hsl(var(--accent));
  color: hsl(var(--accent-foreground));
}

[cmdk-item][data-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}

[cmdk-item][data-selected=true] svg {
  color: hsl(var(--primary));
}
    `}</style>
  )
}

export default function QuickSwitch() {
  const { t } = useTranslation()

  const pubkey = useCurrentPubkey()
  const account = useAtomValue(accountAtom)
  const pomegranateAuth = useAtomValue(pomegranateAuthAtom)
  const { open, setOpen } = useCmdk()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [profiles, setProfiles] = useState<string[]>(pubkey ? [pubkey] : [])
  const fl = useAtomValue(unwrappedFollowListAtom)
  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)
  const includeReplies = useAtomValue(includeRepliesAtom)
  const setIncludeReplies = useSetAtom(includeRepliesAtom)
  const hideNotifications = useAtomValue(hideNotificationsAtom)
  const hideConversations = useAtomValue(hideConversationsAtom)
  const hideFollowing = useAtomValue(hideFollowingAtom)
  const hideFavoriteRelays = useAtomValue(hideFavoriteRelaysAtom)
  const { navigate } = usePrimaryPage()
  const { push } = useSecondaryPage()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 600)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let cancelled = false
    if (!debouncedSearch || debouncedSearch.length < 3) {
      setProfiles(pubkey ? [pubkey] : [])
      return
    }

    // nip05 resolves to a single entity, show it first and skip generic search.
    if (isNip05(debouncedSearch)) {
      queryProfile(debouncedSearch.trim())
        .then(async (p) => {
          if (cancelled) return
          if (!p) return
          const nu = await loadNostrUser({ pubkey: p.pubkey, relays: p?.relays })
          addUsernameToIndex(nu)
          setProfiles([nu.pubkey])
        })
        .catch((error) => console.warn('failed to resolve profile:', getErrorMessage(error)))
      return () => {
        cancelled = true
      }
    }

    const local = searchPubKeysFromLocal(debouncedSearch, 5)
    if (fl?.length) {
      local.sort((a, b) =>
        fl.includes(a) && !fl.includes(b) ? -1 : !fl.includes(a) && fl.includes(b) ? 1 : 0
      )
    }
    const initial = local.slice(0, 4)
    setProfiles(initial)

    if (debouncedSearch.length > 4) {
      searchProfiles(window.hallway.universe.profileSearchRelayUrls, {
        search: debouncedSearch,
        limit: 10
      }).then((remote) => {
        if (cancelled) return
        for (let i = 0; i < remote.length; i++) {
          const rnu = remote[i]
          if (initial.find((pk) => pk === rnu.pubkey)) continue
          initial.push(rnu.pubkey)
          if (initial.length >= 10) break
        }
        setProfiles([...initial])
      })
    }

    resolveProfileInput(debouncedSearch)
      .then((req) => loadNostrUser(req))
      .then((profile) => {
        if (cancelled) return
        addUsernameToIndex(profile)
        if (!initial.find((pk) => pk === profile.pubkey)) {
          initial.unshift(profile.pubkey)
          setProfiles([...initial])
        }
      })
      .catch((error) => console.warn('failed to resolve profile input:', getErrorMessage(error)))

    return () => {
      cancelled = true
    }
  }, [debouncedSearch, pubkey, fl])

  const hardcodedFeeds = useMemo(() => window.hallway.universe.hardcodedFeeds, [])

  // single-relay hardcoded feeds are listed alongside regular relays so they
  // show up in the search index and can be selected individually.
  const hardcodedRelayUrls = useMemo(() => {
    const out: string[] = []
    for (const item of hardcodedFeeds) {
      if (item.relays && item.relays.length === 1 && (item.pubkeys?.length ?? 0) === 0) {
        out.push(item.relays[0])
      }
    }
    return out
  }, [hardcodedFeeds])

  // multi-source hardcoded feeds (object form) — shown as a single grouped item each.
  const hardcodedMultiFeeds = useMemo(
    () =>
      hardcodedFeeds
        .map((item, index) => ({ item, index }))
        .filter(
          ({ item }) =>
            (item.relays?.length ?? 0) + (item.pubkeys?.length ?? 0) > 1 ||
            (item.pubkeys?.length ?? 0) > 0 ||
            ((item.relays?.length ?? 0) === 1 && (item.name || item.icon))
        ),
    [hardcodedFeeds]
  )

  const relayUrls = useMemo(() => {
    const favoriteURLs = favorites
      ? favorites.filter((f): f is string => typeof f === 'string')
      : []
    return [...new Set([...(favoriteURLs || []), ...hardcodedRelayUrls].filter(Boolean))]
  }, [favorites, hardcodedRelayUrls])

  const [relayIndex, setRelayIndex] = useState<FlexSearch.Index | null>(null)

  useEffect(() => {
    const index = new FlexSearch.Index({ tokenize: 'forward' })
    for (const url of relayUrls) index.add(url, `${simplifyUrl(url)} ${url}`)
    setRelayIndex(index)
  }, [relayUrls])

  const filteredRelayUrls = useMemo(() => {
    if (!search || !relayIndex) return relayUrls
    return relayIndex.search(search, { limit: 20 }) as string[]
  }, [search, relayIndex, relayUrls])

  const [feedIndex, setFeedIndex] = useState<FlexSearch.Index | null>(null)

  useEffect(() => {
    const idx = new FlexSearch.Index({ tokenize: 'forward' })
    for (const { item, index } of hardcodedMultiFeeds) {
      idx.add(index, `${item.name ?? ''} ${(item.relays ?? []).map(simplifyUrl).join(' ')}`)
    }
    setFeedIndex(idx)
  }, [hardcodedMultiFeeds])

  const filteredHardcodedMultiFeeds = useMemo(() => {
    if (!search || !feedIndex) return hardcodedMultiFeeds
    const ids = feedIndex.search(search, { limit: 20 }) as number[]
    return ids.map((i) => hardcodedMultiFeeds[i]).filter(Boolean)
  }, [search, feedIndex, hardcodedMultiFeeds])

  const filteredRelaySets = useMemo(() => {
    let favoriteRelaySets: ResolvedSet<string>[] = []
    if (favorites)
      favoriteRelaySets = favorites.filter(
        (f): f is ResolvedSet<string> => typeof f !== 'string' && f.items.length > 0
      )
    if (!search) return favoriteRelaySets

    return favoriteRelaySets.filter((s) => s.title.toLowerCase().includes(search.toLowerCase()))
  }, [search, favorites])

  const searchLower = search.toLowerCase()

  const matchesSearch = (...values: string[]) =>
    !search || values.some((value) => value.toLowerCase().includes(searchLower))

  const feedsVisible =
    !!pubkey &&
    ((!hideFollowing &&
      (matchesSearch(t('Following'), 'following') ||
        matchesSearch(
          t('Following (only root notes)'),
          t('Following (with replies)'),
          'following',
          'with replies',
          'reply roots'
        ))) ||
      (!hideConversations && matchesSearch(t('Conversations'), 'conversations')) ||
      (!hideNotifications && matchesSearch(t('Notifications'), 'notifications')))

  const directItems = useMemo<{ kind: string; id: string; label: string }[]>(() => {
    if (!search) return []

    const s = search.trim()

    if (/^[0-9a-f]{64}$/.test(s)) {
      return [{ kind: 'note', id: s, label: 'Open as note' }]
    }

    try {
      let id = s
      if (s.startsWith('nostr:')) id = s.slice(6)
      const decoded = decode(id)
      if (['nevent', 'naddr', 'note'].includes(decoded.type)) {
        return [{ kind: 'note', id, label: 'Open note' }]
      }
      if (decoded.type === 'npub') {
        return [{ kind: 'profile', id: decoded.data as string, label: 'Open profile' }]
      }
      if (decoded.type === 'nprofile') {
        return [
          {
            kind: 'profile',
            id: (decoded.data as { pubkey: string }).pubkey,
            label: 'Open profile'
          }
        ]
      }
    } catch {
      /***/
    }

    if (!s.includes(' ') && (s.includes('.') || s.includes('/'))) {
      let url = s
      if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
        url = 'wss://' + url
      }
      try {
        const normalized = normalizeUrl(url)
        if (normalized) return [{ kind: 'relay', id: normalized, label: simplifyUrl(normalized) }]
      } catch {
        /***/
      }
    }

    return []
  }, [search])

  const relayResults = filteredRelayUrls.length > 0 && (
    <Command.Group heading="Relays">
      {filteredRelayUrls.map((url) => (
        <Command.Item
          key={url}
          onSelect={() =>
            onSelect(() => {
              navigate('home')
              switchFeed({ relay: url })
            })
          }
        >
          <RelayIcon url={url} iconSize={10} className="w-5 h-5 shrink-0" />
          <span>{simplifyUrl(url)}</span>
        </Command.Item>
      ))}
    </Command.Group>
  )

  const relaySetsResults = filteredRelaySets.length > 0 && !hideFavoriteRelays && (
    <Command.Group heading="Relay Sets">
      {filteredRelaySets.map((set) => (
        <Command.Item
          key={set.pointer.identifier}
          onSelect={() =>
            onSelect(() => {
              navigate('home')
              switchFeed({
                relaySet: set
              })
            })
          }
        >
          <HugeiconsIcon icon={Folder01Icon} size={10} className="w-5 h-5 shrink-0" />
          <span>{set.title}</span>
        </Command.Item>
      ))}
    </Command.Group>
  )

  const fixedFeedResults = filteredHardcodedMultiFeeds.length > 0 && (
    <Command.Group heading="Fixed Feeds">
      {filteredHardcodedMultiFeeds.map(({ item, index }) => (
        <HardcodedFeedListItem
          key={`hf:${index}`}
          item={item}
          onSelect={() => {
            navigate('home')
            switchFeed({ hardcodedFeed: index })
          }}
        />
      ))}
    </Command.Group>
  )

  const close = () => {
    setOpen(false)
    setSearch('')
  }

  const onSelect = (action: () => void) => {
    action()
    close()
  }

  return (
    <>
      <CmdkStyles />
      <Command.Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setSearch('')
        }}
        shouldFilter={false}
      >
        <div cmdk-input-wrapper="">
          <HugeiconsIcon icon={Search01Icon} className="size-4 text-muted-foreground shrink-0" />
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder={t('Search relay, profile, paste event code...')}
          />
        </div>
        <Command.List>
          <Command.Empty>{t('No results found.')}</Command.Empty>

          {search && relayResults}
          {search && relaySetsResults}
          {search && fixedFeedResults}

          {feedsVisible && (
            <Command.Group heading="Feeds">
              {!hideFollowing && matchesSearch(t('Following'), 'following') && (
                <Command.Item
                  onSelect={() =>
                    onSelect(() => {
                      navigate('home')
                      switchFeed({ pubkey })
                    })
                  }
                >
                  <HugeiconsIcon icon={Contact01Icon} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Following')}</span>
                </Command.Item>
              )}
              {!hideFollowing &&
                matchesSearch(
                  t('Following (only root notes)'),
                  t('Following (with replies)'),
                  'following',
                  'with replies',
                  'reply roots'
                ) && (
                  <Command.Item
                    onSelect={() =>
                      onSelect(() => {
                        navigate('home')
                        switchFeed({ pubkey })
                        setIncludeReplies(includeReplies ? false : true)
                      })
                    }
                  >
                    <HugeiconsIcon icon={Contact01Icon} className="mr-2 h-4 w-4 shrink-0" />
                    <span>
                      {includeReplies
                        ? t('Following (only root notes)')
                        : t('Following (with replies)')}
                    </span>
                  </Command.Item>
                )}
              {!hideConversations && matchesSearch(t('Conversations'), 'conversations') && (
                <Command.Item onSelect={() => onSelect(() => navigate('conversations'))}>
                  <HugeiconsIcon icon={Comment} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Conversations')}</span>
                </Command.Item>
              )}
              {!hideNotifications && matchesSearch(t('Notifications'), 'notifications') && (
                <Command.Item onSelect={() => onSelect(() => navigate('notifications'))}>
                  <HugeiconsIcon icon={BellIcon} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Notifications')}</span>
                </Command.Item>
              )}
            </Command.Group>
          )}

          {!search && relayResults}
          {!search && relaySetsResults}
          {!search && fixedFeedResults}

          {directItems.length > 0 && (
            <Command.Group heading="Go to">
              {directItems.map((item) => (
                <Command.Item
                  key={item.id}
                  onSelect={() =>
                    onSelect(() => {
                      if (item.kind === 'note') {
                        push(toNote(item.id))
                      } else if (item.kind === 'profile') {
                        push(toProfile(item.id))
                      } else if (item.kind === 'relay') {
                        navigate('home')
                        switchFeed({ relay: item.id })
                      }
                    })
                  }
                >
                  {item.kind === 'note' && (
                    <HugeiconsIcon icon={Notebook01Icon} className="mr-2 h-4 w-4 shrink-0" />
                  )}
                  {item.kind === 'profile' && (
                    <HugeiconsIcon icon={UserIcon} className="mr-2 h-4 w-4 shrink-0" />
                  )}
                  {item.kind === 'relay' && (
                    <HugeiconsIcon icon={ServerStack01Icon} className="mr-2 h-4 w-4 shrink-0" />
                  )}
                  <span>{item.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {pubkey && (
            <Command.Group>
              {matchesSearch(t('Database Inspector'), 'database') && (
                <Command.Item
                  onSelect={() =>
                    onSelect(() => {
                      navigate('database')
                    })
                  }
                >
                  <HugeiconsIcon icon={DatabaseSearchIcon} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Database Inspector')}</span>
                </Command.Item>
              )}
              {matchesSearch(t('Theme'), 'theme', 'colors', 'appearance') && (
                <Command.Item onSelect={() => onSelect(() => push('/theme'))}>
                  <HugeiconsIcon icon={PaintBrushIcon} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Theme')}</span>
                </Command.Item>
              )}
              {account?.signerType === 'nsec' &&
                account.nsec &&
                matchesSearch(
                  t('Export private key'),
                  'private key',
                  'nsec',
                  'backup',
                  'export'
                ) && (
                  <Command.Item onSelect={() => onSelect(() => openSaveNsecModal())}>
                    <HugeiconsIcon icon={SaveIcon} className="mr-2 h-4 w-4 shrink-0" />
                    <span>{t('Export private key')}</span>
                  </Command.Item>
                )}
              {!!pomegranateAuth || (account?.signerType === 'nsec' && !!account.nsec)
                ? matchesSearch(t('Bunker'), 'bunker', 'hosted', 'management', 'pomegranate') && (
                    <Command.Item onSelect={() => onSelect(() => push('/pomegranate'))}>
                      <HugeiconsIcon icon={Key01Icon} className="mr-2 h-4 w-4 shrink-0" />
                      <span>{t('Bunker')}</span>
                    </Command.Item>
                  )
                : null}
              {matchesSearch(
                t('Fork this instance'),
                'fork',
                'nsite',
                'deploy',
                'nostr',
                'site'
              ) && (
                <Command.Item onSelect={() => onSelect(() => push('/fork'))}>
                  <HugeiconsIcon icon={SourceCodeIcon} className="mr-2 h-4 w-4 shrink-0" />
                  <span>{t('Fork this instance')}</span>
                </Command.Item>
              )}
            </Command.Group>
          )}

          <Command.Group>
            {search && (
              <Command.Item
                onSelect={() =>
                  onSelect(() => {
                    navigate('search', { input: search })
                  })
                }
              >
                <HugeiconsIcon icon={Search01Icon} className="mr-2 h-4 w-4 shrink-0" />
                <span>{t('Search “{{term}}”', { term: search })}</span>
              </Command.Item>
            )}
            {search && search === searchLower && !search.includes(' ') && (
              <Command.Item
                onSelect={() =>
                  onSelect(() => {
                    navigate('search', { input: `#${search}` })
                  })
                }
              >
                <HugeiconsIcon icon={HashIcon} className="mr-2 h-4 w-4 shrink-0" />
                <span>Tag: #{search}</span>
              </Command.Item>
            )}
          </Command.Group>

          {profiles.length > 0 && (
            <Command.Group heading="Profiles">
              {profiles.map((pubkey) => (
                <Command.Item
                  key={pubkey}
                  value={pubkey}
                  onSelect={() =>
                    onSelect(() => {
                      push(toProfile(pubkey))
                    })
                  }
                >
                  <UserItem
                    user={pubkey}
                    hideFollowButton
                    showFollowingBadge
                    className="pointer-events-none"
                  />
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command.Dialog>
    </>
  )
}
